/**
 * AccountInfoSettings — self-service account info (company, phone).
 *
 * Phone is editable by any authenticated user. Company is editable only by
 * admin-tier roles (platform_admin, parent_org_admin, customer_admin); the
 * backend enforces this with a 403, and we mirror that here by disabling the
 * field for non-admin roles.
 */

import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getMyProfile, updateMyProfile } from '../../services/profileApi';
import { useAuthStore } from '../../store/authStore';

const ADMIN_ROLES = new Set(['platform_admin', 'parent_org_admin', 'customer_admin']);

export function AccountInfoSettings() {
  const role = useAuthStore((s) => s.user?.role);
  const canEditCompany = role ? ADMIN_ROLES.has(role) : false;

  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['me-profile'], queryFn: getMyProfile });

  const [companyName, setCompanyName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (data) {
      setCompanyName(data.company_name ?? '');
      setPhoneNumber(data.phone_number ?? '');
    }
  }, [data]);

  const mutation = useMutation({
    mutationFn: async () => {
      const patch: { phone_number: string | null; company_name?: string | null } = {
        phone_number: phoneNumber.trim() === '' ? null : phoneNumber.trim(),
      };
      if (canEditCompany) {
        patch.company_name = companyName.trim() === '' ? null : companyName.trim();
      }
      return updateMyProfile(patch);
    },
    onSuccess: () => {
      setError(null);
      setMessage('Saved');
      queryClient.invalidateQueries({ queryKey: ['me-profile'] });
    },
    onError: (err: any) => {
      setMessage(null);
      setError(err?.response?.data?.error || 'Failed to save profile');
    },
  });

  return (
    <section className="bg-ems-bg-card rounded-2xl border border-ems-border p-6 mb-6">
      <header className="mb-4">
        <h2 className="text-lg font-semibold text-ems-text-primary">Account Info</h2>
        <p className="text-sm text-ems-text-muted mt-1">
          Your contact information. Phone is always editable; company is managed by your administrator.
        </p>
      </header>

      {isLoading ? (
        <div className="text-sm text-ems-text-muted">Loading…</div>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate();
          }}
          className="space-y-4"
        >
          {message && (
            <div className="p-3 rounded-lg bg-ems-green/5 border border-ems-green/20 text-ems-green text-sm">{message}</div>
          )}
          {error && (
            <div className="p-3 rounded-lg bg-ems-red/10 border border-ems-red/20 text-ems-red text-sm">{error}</div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-ems-text-primary mb-1">Company Name</label>
              <input
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                disabled={!canEditCompany}
                className="w-full px-3 py-2 rounded-lg border border-ems-border text-sm bg-ems-bg text-ems-text-primary outline-none focus:border-ems-green disabled:opacity-60 disabled:cursor-not-allowed"
              />
              {!canEditCompany && (
                <p className="text-xs text-ems-text-muted mt-1">Managed by your administrator</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-ems-text-primary mb-1">Phone Number</label>
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-ems-border text-sm bg-ems-bg text-ems-text-primary outline-none focus:border-ems-green"
              />
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={mutation.isPending}
              className="px-4 py-2 rounded-lg bg-ems-forest text-ems-green border border-ems-green/20 text-sm font-medium hover:bg-ems-forest/80 disabled:opacity-50"
            >
              {mutation.isPending ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
